import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Building, Users, Mail } from 'lucide-react';

const SobrePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1A3A5F] to-[#4D8BC9] text-white">
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Sobre Nós</h1>
            <p className="text-xl mb-8">
              Conheça o Grupo M7 CORP e nossa trajetória de excelência em engenharia e inspeção industrial
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quem Somos */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="md:w-1/2"
            >
              <h2 className="text-3xl font-bold mb-6 text-[#1A3A5F]">Quem Somos</h2>
              <p className="text-lg text-gray-700 mb-4">
                O Grupo M7 CORP é um conglomerado empresarial especializado em soluções de engenharia, 
                inspeção e integridade para o setor industrial. Fundado em 2005, o grupo reúne empresas 
                que se destacam pela excelência técnica, inovação e compromisso com a qualidade dos 
                serviços prestados.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Com mais de 15 anos de experiência no mercado, o Grupo M7 CORP atende diversos setores 
                industriais, como petróleo e gás, energia, química e petroquímica, papel e celulose, 
                alimentos e bebidas, siderurgia e metalurgia, entre outros.
              </p>
              <p className="text-lg text-gray-700">
                Nossa missão é contribuir para a segurança operacional, a eficiência dos processos e o 
                desenvolvimento tecnológico de nossos clientes, através de soluções integradas que garantam 
                conformidade com as normas técnicas e regulamentações aplicáveis.
              </p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="md:w-1/2"
            >
              <div className="bg-gray-100 p-8 rounded-lg">
                <h3 className="text-2xl font-bold mb-4 text-[#1A3A5F]">Nossos Valores</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="bg-[#4D8BC9] rounded-full p-1 mt-1 mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1A3A5F]">Excelência Técnica</h4>
                      <p className="text-gray-700">Buscamos a excelência em tudo o que fazemos, garantindo a qualidade e confiabilidade dos nossos serviços.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-[#4D8BC9] rounded-full p-1 mt-1 mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1A3A5F]">Inovação</h4>
                      <p className="text-gray-700">Investimos constantemente em novas tecnologias e metodologias para oferecer soluções inovadoras e eficientes.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-[#4D8BC9] rounded-full p-1 mt-1 mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1A3A5F]">Ética e Integridade</h4>
                      <p className="text-gray-700">Atuamos com ética, transparência e integridade em todas as nossas relações, respeitando as pessoas e o meio ambiente.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-[#4D8BC9] rounded-full p-1 mt-1 mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1A3A5F]">Compromisso com o Cliente</h4>
                      <p className="text-gray-700">Trabalhamos em parceria com nossos clientes, buscando entender suas necessidades e superar suas expectativas.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nossa História */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-[#1A3A5F]">Nossa História</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Conheça a trajetória do Grupo M7 CORP e como nos tornamos referência em engenharia e inspeção industrial.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="border-l-4 border-[#4D8BC9] pl-8 space-y-12">
              {/* Marco 1 */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute -left-12 w-8 h-8 bg-[#4D8BC9] rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">2005</h3>
                  <h4 className="text-lg font-semibold mb-2 text-[#4D8BC9]">Fundação da MAIN'TECH PLUS</h4>
                  <p className="text-gray-700">
                    Fundação da MAIN'TECH PLUS, empresa especializada em inspeção e integridade de equipamentos industriais, 
                    com foco em caldeiras, vasos de pressão e tubulações.
                  </p>
                </div>
              </motion.div>

              {/* Marco 2 */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute -left-12 w-8 h-8 bg-[#4D8BC9] rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">2010</h3>
                  <h4 className="text-lg font-semibold mb-2 text-[#4D8BC9]">Expansão de Serviços</h4>
                  <p className="text-gray-700">
                    Expansão dos serviços da MAIN'TECH PLUS, incluindo ensaios não destrutivos, análise de integridade 
                    e adequação a normativas técnicas.
                  </p>
                </div>
              </motion.div>

              {/* Marco 3 */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute -left-12 w-8 h-8 bg-[#4D8BC9] rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">2015</h3>
                  <h4 className="text-lg font-semibold mb-2 text-[#4D8BC9]">Fundação da IADG EXPERTS</h4>
                  <p className="text-gray-700">
                    Fundação da IADG EXPERTS, empresa especializada em engenharia e consultoria industrial, 
                    com foco em projetos, cálculos e dimensionamento de equipamentos.
                  </p>
                </div>
              </motion.div>

              {/* Marco 4 */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute -left-12 w-8 h-8 bg-[#4D8BC9] rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">2018</h3>
                  <h4 className="text-lg font-semibold mb-2 text-[#4D8BC9]">Criação do Grupo M7 CORP</h4>
                  <p className="text-gray-700">
                    Criação do Grupo M7 CORP, reunindo as empresas MAIN'TECH PLUS e IADG EXPERTS sob uma mesma 
                    estrutura corporativa, visando oferecer soluções integradas para o setor industrial.
                  </p>
                </div>
              </motion.div>

              {/* Marco 5 */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute -left-12 w-8 h-8 bg-[#4D8BC9] rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">2020</h3>
                  <h4 className="text-lg font-semibold mb-2 text-[#4D8BC9]">Expansão Nacional</h4>
                  <p className="text-gray-700">
                    Expansão das operações do Grupo M7 CORP para todo o território nacional, com a abertura 
                    de escritórios regionais e a ampliação da equipe técnica.
                  </p>
                </div>
              </motion.div>

              {/* Marco 6 */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute -left-12 w-8 h-8 bg-[#4D8BC9] rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">2023</h3>
                  <h4 className="text-lg font-semibold mb-2 text-[#4D8BC9]">Inovação Tecnológica</h4>
                  <p className="text-gray-700">
                    Investimento em inovação tecnológica, com a implementação de novas metodologias, 
                    softwares avançados e equipamentos de última geração para diagnósticos mais precisos 
                    e análises detalhadas.
                  </p>
                </div>
              </motion.div>

              {/* Marco 7 */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute -left-12 w-8 h-8 bg-[#4D8BC9] rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">2025</h3>
                  <h4 className="text-lg font-semibold mb-2 text-[#4D8BC9]">Presente e Futuro</h4>
                  <p className="text-gray-700">
                    Atualmente, o Grupo M7 CORP continua sua trajetória de crescimento e inovação, 
                    buscando sempre oferecer as melhores soluções para seus clientes e contribuir para 
                    o desenvolvimento do setor industrial brasileiro.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipe */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-[#1A3A5F]">Nossa Equipe</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Conheça os profissionais que fazem parte do Grupo M7 CORP e que trabalham diariamente 
              para oferecer as melhores soluções para nossos clientes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Membro 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="h-64 bg-[#1A3A5F] flex items-center justify-center">
                <Users className="h-32 w-32 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1 text-[#1A3A5F]">Carlos Mendes</h3>
                <p className="text-[#4D8BC9] font-semibold mb-3">CEO e Fundador</p>
                <p className="text-gray-700 mb-4">
                  Engenheiro Mecânico com mais de 25 anos de experiência no setor industrial, 
                  especialista em gestão de empresas de engenharia e inspeção.
                </p>
              </div>
            </motion.div>

            {/* Membro 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="h-64 bg-[#1A3A5F] flex items-center justify-center">
                <Users className="h-32 w-32 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1 text-[#1A3A5F]">Ana Paula Silva</h3>
                <p className="text-[#4D8BC9] font-semibold mb-3">Diretora Técnica</p>
                <p className="text-gray-700 mb-4">
                  Engenheira Mecânica com especialização em Integridade de Equipamentos, 
                  responsável pela gestão técnica e garantia da qualidade dos serviços.
                </p>
              </div>
            </motion.div>

            {/* Membro 3 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="h-64 bg-[#1A3A5F] flex items-center justify-center">
                <Users className="h-32 w-32 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-1 text-[#1A3A5F]">Roberto Almeida</h3>
                <p className="text-[#4D8BC9] font-semibold mb-3">Diretor de Operações</p>
                <p className="text-gray-700 mb-4">
                  Engenheiro de Produção com MBA em Gestão de Projetos, responsável pelo 
                  planejamento e execução das operações do grupo.
                </p>
              </div>
            </motion.div>
          </div>

          <div className="text-center mt-10">
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Nossa equipe é composta por mais de 100 profissionais altamente qualificados, 
              incluindo engenheiros, técnicos, inspetores e consultores, todos comprometidos 
              com a excelência e a satisfação dos nossos clientes.
            </p>
          </div>
        </div>
      </section>

      {/* Informações Corporativas */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-[#1A3A5F]">Informações Corporativas</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Conheça mais sobre a estrutura corporativa do Grupo M7 CORP.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Bloco 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-md"
            >
              <div className="flex items-center mb-4">
                <Building className="h-8 w-8 text-[#1A3A5F] mr-3" />
                <h3 className="text-xl font-bold text-[#1A3A5F]">Sede</h3>
              </div>
              <p className="text-gray-700">
                Av. Paulista, 1000<br />
                São Paulo - SP<br />
                CEP: 01310-100<br />
                Brasil
              </p>
            </motion.div>

            {/* Bloco 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-md"
            >
              <div className="flex items-center mb-4">
                <Users className="h-8 w-8 text-[#1A3A5F] mr-3" />
                <h3 className="text-xl font-bold text-[#1A3A5F]">Empresas do Grupo</h3>
              </div>
              <ul className="text-gray-700 space-y-2">
                <li>MAIN'TECH PLUS</li>
                <li>IADG EXPERTS</li>
              </ul>
            </motion.div>

            {/* Bloco 3 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-md"
            >
              <div className="flex items-center mb-4">
                <Mail className="h-8 w-8 text-[#1A3A5F] mr-3" />
                <h3 className="text-xl font-bold text-[#1A3A5F]">Contato</h3>
              </div>
              <p className="text-gray-700">
                Telefone: (11) 3456-7890<br />
                E-mail: contato@m7corp.com.br<br />
                Website: www.m7corp.com.br
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A3A5F] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Pronto para trabalhar conosco?</h2>
            <p className="text-xl mb-8">
              Entre em contato e descubra como podemos ajudar sua empresa a garantir segurança, 
              conformidade e eficiência operacional.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link 
                to="/contato" 
                className="bg-white text-[#1A3A5F] hover:bg-gray-100 font-bold px-8 py-3 rounded-md transition-colors"
              >
                Fale Conosco
              </Link>
              <Link 
                to="/servicos" 
                className="bg-transparent border-2 border-white hover:bg-white hover:text-[#1A3A5F] text-white font-bold px-8 py-3 rounded-md transition-colors"
              >
                Nossos Serviços
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SobrePage;
